export interface ConnectionRow {
  zone: string;
  state: string;
  sdwan: string;
  type: string;
  network: string;
  device: string;
  protocol: string;
  mac: string;
  address: string;
  externalIp: string;
  isp: string;
  action: string;
  uptime: string;
  metrics?: string;
  detail?: boolean;
  usageKeys: string[];
  disabled?: boolean;
  cumulativeBytes?: number;
}

const list = (value: any): any[] => Array.isArray(value) ? value : Object.keys(value || {}).map(key => ({ name: key, ...value[key] }));
const state = (value: any): string => {
  if (value == null || value === '') { return ''; }
  const text = String(value).toLowerCase();
  if (['true', '1', 'up', 'active', 'running'].includes(text)) { return 'Up'; }
  if (['sleep', 'sleeping'].includes(text)) { return 'Sleep'; }
  return 'Down';
};
const duration = (value: any): string => {
  if (value == null || value === '') { return ''; }
  if (!Number.isFinite(Number(value))) { return String(value); }
  const seconds = Math.max(0, Math.floor(Number(value)));
  const days = Math.floor(seconds / 86400);
  return `${days ? days + 'd ' : ''}${Math.floor(seconds / 3600) % 24}h ${Math.floor(seconds / 60) % 60}m ${seconds % 60}s`;
};
const counter = (source: any): number => {
  const value = source.total_bytes != null ? Number(source.total_bytes)
    : source.tx_bytes != null || source.rx_bytes != null ? Number(source.tx_bytes || 0) + Number(source.rx_bytes || 0) : NaN;
  return Number.isFinite(value) && value >= 0 ? value : undefined;
};
const keys = (device: string, link?: string): string[] => [link && device ? `${link} ${device} Transferred` : '', device ? `${device} Transferred` : ''].filter(Boolean);

function row(source: any, extra: Partial<ConnectionRow> = {}): ConnectionRow {
  return {
    zone: source.zone || '', state: state(source.state ?? source.up), sdwan: source.link || source.sdwan_link || '',
    type: source.type || '', network: source.network || source.interface || source.ifname || '',
    device: source.ifname || '', protocol: source.proto || source.protocol || '', mac: source.mac || '',
    address: source.address || '', externalIp: source.isp_info?.ip || '', isp: source.isp_info?.isp || '',
    action: source.action || '', uptime: duration(source.uptime ?? source.uptime_sec),
    cumulativeBytes: counter(source),
    usageKeys: keys(source.ifname, source.link), disabled: source.action === 'disabled', ...extra
  };
}

function detail(source: any, parent: ConnectionRow, lane = false, settings: any = {}): ConnectionRow {
  const label = lane ? (source.default || source.mode_str === 'default' ? 'laneDef' : `lane${source.lane ?? ''}`) : parent.sdwan;
  const metric = (value: any, suffix: string) => value == null ? '—' : `${value}${suffix}`;
  const ratio = source.orig_dupes ?? (source.k != null && source.n != null ? `${source.k}:${Math.max(0, Number(source.n) - Number(source.k))}` : '');
  const isDefault = source.default || source.mode_str === 'default';
  const configuredClass = String(settings[`fec_q${source.lane}_steer`] || '').split(':')[1];
  const qclass = isDefault ? '' : source.q_class ?? source.class ?? configuredClass;
  return row(source, {
    zone: '', sdwan: `.${label}`, type: lane ? 'lane' : 'transport', detail: true,
    network: lane ? '' : parent.network, device: lane ? '' : parent.device,
    protocol: lane ? source.mode_str || source.mode || '' : source.protocol || source.proto || 'udp',
    metrics: `Latency: ${metric(source.rtt_avg_ms ?? source.decode_latency_avg_ms, 'ms')} | Jitter: ${metric(source.rtt_jitter_ms ?? source.jitter_ms, 'ms')} | Loss: ${metric(source.loss_pct ?? source.post_fec_loss_pct ?? source.pre_fec_loss_pct, '%')} | MOS: ${metric(source.mos, '')}`,
    action: lane ? (ratio ? `fec ${ratio}${qclass ? ` q${qclass}` : ''}` : '') : `${source.action || parent.action}${source.weight != null || source.rx_weight != null ? ` ${source.weight ?? '—'}/${source.rx_weight ?? '—'}` : ''}`,
    uptime: '',
    // Detail counters must never fall back to the parent interface's usage.
    usageKeys: [`${parent.sdwan} ${label} Transferred`, `.${label} Transferred`]
  });
}

/** Join SDWAN configuration with interface and midlay telemetry without mutating the device. */
export function buildConnectionRows(statistics: any): ConnectionRow[] {
  const interfaces = list(statistics.interfaces);
  const links = list(statistics.teams).reduce((all, team) => all.concat(list(team.links)), []);
  const midlay = statistics.rrt_midlay || {};
  const overlays = midlay.dpcs || midlay.peers ? [...list(midlay.dpcs), ...list(midlay.peers)] : list(midlay);
  const config = (item: any) => item.team_config || item.teamConfig || item.vce_provision || {};
  // Some vHUB payloads publish DPC interfaces without the legacy teams collection.
  overlays.forEach(overlay => {
    const settings = config(overlay);
    const iface = overlay.iface || settings.iface;
    if (iface && !links.some(link => link.ifname === iface)) {
      links.push({ ...overlay, ifname: iface, link: settings.sdwan_link || overlay.sdwan_link,
        type: 'overlayplus', action: 'overlayplus' });
    }
  });
  const used = new Set<any>();
  const rows: ConnectionRow[] = [];
  links.forEach(link => {
    const iface = interfaces.find(item => (link.ifname && item.ifname === link.ifname) || (link.interface && item.network === link.interface));
    if (iface) { used.add(iface); }
    const parent = row({ ...iface, ...link }, {
      zone: iface?.zone || 'wan',
      type: link.type === 'underlay' ? link.action : link.type === 'overlay' ? link.action || link.type : link.type,
      network: iface?.network || link.interface || link.ifname,
      device: iface?.ifname || link.ifname
    });
    rows.push(parent);
    overlays.forEach(overlay => {
      const settings = config(overlay);
      list(overlay.links).filter(transport => transport.name === link.link || transport.name === link.ifname).forEach(transport => {
        rows.push(detail(transport, parent));
      });
      if ([overlay.iface, overlay.name, settings.iface].includes(link.ifname) || settings.sdwan_link === link.link) {
        parent.isp = settings.remote_addr || overlay.server_url || parent.isp;
        // Diagnostics presents the midlay endpoint, not the underlay IP/ISP lookup.
        parent.mac = '';
        parent.address = '';
        parent.externalIp = '';
        if (overlay.uptime_sec != null) { parent.uptime = duration(overlay.uptime_sec); }
        const lanes = list(overlay.qfec_lanes || overlay.fec_lanes || overlay.lanes);
        if (lanes.length && lanes.every(lane => counter(lane) != null)) {
          parent.cumulativeBytes = lanes.reduce((sum, lane) => sum + counter(lane), 0);
        }
        lanes.forEach(lane => rows.push(detail(lane, parent, true, settings)));
      }
    });
  });
  // Interfaces include LAN, WiFi, and RAS on both devices and vHUBs.
  interfaces.filter(iface => !used.has(iface) && iface.zone).forEach(iface => {
    const wifi = list(statistics.wifi).find(item => item.ifname && item.ifname === iface.ifname);
    rows.push(row({ ...wifi, ...iface }, {
      type: iface.zone === 'ras' ? 'RAS' : wifi ? 'WiFi' : iface.type,
      action: iface.action || wifi?.mode || iface.mode || iface.dpc || (iface.zone === 'ras' && /^wg\d+$/.test(iface.ifname) ? `DPC${iface.ifname.slice(2)}` : ''),
      externalIp: iface.ssid || wifi?.ssid || iface.isp_info?.ip || ''
    }));
  });
  // Older telemetry stores local interfaces separately.
  const addLocal = (source: any, type: string, network: string) => {
    if (!source || !source.ifname || rows.some(item => !item.detail && item.device === source.ifname)) { return; }
    rows.push(row(source, { zone: source.zone || 'lan', type: source.type || type, network: source.network || network, externalIp: source.ssid || '', isp: source.name || '', action: source.mode || '' }));
  };
  Object.keys(statistics.bridges || {}).forEach(name => addLocal(statistics.bridges[name], 'bridge', name));
  Object.keys(statistics.wifi || {}).forEach(name => addLocal(statistics.wifi[name], 'WiFi', name.includes('guest') ? 'guest' : 'lan'));
  const rank = (item: ConnectionRow) => item.zone === 'wan'
    ? (item.type === 'backup' ? 2 : /overlay/i.test(item.type) ? 1 : 0)
    : item.zone === 'ras' ? 5 : item.type === 'WiFi' ? 4 : 3;
  // Sort groups, keeping each detail row attached to its parent.
  const groups: ConnectionRow[][] = [];
  rows.forEach(item => item.detail && groups.length ? groups[groups.length - 1].push(item) : groups.push([item]));
  // Limit this summary to the connection groups in the Diagnostics reference.
  // Match identities, not changing state, addresses, counters, or uptime.
  const visibleParent = (item: ConnectionRow) => {
    if (String(item.type).toLowerCase() === 'vlan') { return false; }
    if (item.sdwan) { return ['link1', 'link2', 'link3', 'linkB1'].includes(item.sdwan); }
    return (item.type === 'bridge' && ['lan', 'br-lan'].includes(item.device))
      || (item.type === 'WiFi' && ['wlan0', 'wlan1'].includes(item.device))
      || (item.type === 'RAS' && item.device === 'wg0');
  };
  return groups.filter(group => visibleParent(group[0]))
    .map(group => group.filter((item, index) => index === 0
      || (group[0].sdwan === 'link3' && item.type === 'lane'
        && ['.lane1', '.lane2', '.lane3', '.laneDef'].includes(item.sdwan))
      || (['link1', 'link2', 'linkB1'].includes(group[0].sdwan) && item.type === 'transport')))
    .sort((a, b) => rank(a[0]) - rank(b[0])).reduce((all, group) => all.concat(group), []);
}
